import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import billingApi from '../../../api/billingApi';
import { BillingPlan } from '../../../api/types';

interface AdminBillingPlansModalProps {
    isOpen: boolean;
    workspaceId: string;
    businessName: string;
    currentPlanId?: string | null;
    currentPlanStatus?: string | null;
    onClose: () => void;
}

const emptyPlan: Partial<BillingPlan> = {
    tier: 'core',
    name: '',
    description: '',
    price_cents: 0,
    currency: 'usd',
    interval: 'month',
    features: [],
    stripe_payment_url: '',
    stripe_price_id: '',
    llm_tokens_limit_monthly: 15_000_000,
    doc_units_limit_monthly: 400,
    is_recommended: false,
    is_active: true,
    sort_order: 100,
};

const dollarsFromCents = (cents?: number) => String(((cents || 0) / 100) || '');
const DEFAULT_STANDARD_STRIPE_PRICE_ID = 'price_1T6c2XGXSh4Use7eC3iZ3D0k';
const DEFAULT_CORE_STRIPE_PRICE_ID = 'price_1T6c7DGXSh4Use7ePqDI9EuO';
const DEFAULT_STANDARD_STRIPE_PAYMENT_URL = 'https://buy.stripe.com/test_dRmdR9gKu1D89EFbo25Rm00';
const DEFAULT_CORE_STRIPE_PAYMENT_URL = 'https://buy.stripe.com/test_3cI3cvbqa6Xs04563I5Rm01';

const hydrateDefaultStripeFields = <T extends Partial<BillingPlan>>(plan: T): T => {
    if (plan.plan_id === 'standard' || plan.tier === 'standard') {
        return {
            ...plan,
            stripe_price_id: plan.stripe_price_id || DEFAULT_STANDARD_STRIPE_PRICE_ID,
            stripe_payment_url: plan.stripe_payment_url || DEFAULT_STANDARD_STRIPE_PAYMENT_URL,
        };
    }
    if (plan.plan_id === 'core' || plan.tier === 'core') {
        return {
            ...plan,
            stripe_price_id: plan.stripe_price_id || DEFAULT_CORE_STRIPE_PRICE_ID,
            stripe_payment_url: plan.stripe_payment_url || DEFAULT_CORE_STRIPE_PAYMENT_URL,
        };
    }
    return plan;
};

const PLAN_TEMPLATES: Partial<BillingPlan>[] = [
    {
        plan_id: 'standard',
        tier: 'standard',
        name: 'Starter Plan',
        description: 'Self-serve financial operations from day one.',
        price_cents: 29900,
        interval: 'month',
        features: [
            'Operating Ledger: all accounts and locations, reconciled automatically.',
            'Live Cash Flow: live position plus 13-week forecast.',
            'Margin Control: weekly margin movement with key drivers.',
            'Priority Signals: ranked actions by dollar impact.',
        ],
        is_active: true,
        is_recommended: false,
        sort_order: 10,
        stripe_price_id: DEFAULT_STANDARD_STRIPE_PRICE_ID,
        stripe_payment_url: DEFAULT_STANDARD_STRIPE_PAYMENT_URL,
    },
    {
        plan_id: 'core',
        tier: 'core',
        name: 'Core Plan',
        description: 'Setup and ongoing support from our team.',
        price_cents: 49900,
        interval: 'month',
        features: [
            'Managed Finance Ops: data mapping and ledger cleanup by our team.',
            'Invoice Intelligence: unit price tracking with vendor variance alerts.',
            'Capital Readiness: working capital visibility and lender-ready exports.',
            'Margin Baseline Report: one-time audit to lock in your profit targets.',
        ],
        is_active: true,
        is_recommended: true,
        sort_order: 20,
        stripe_price_id: DEFAULT_CORE_STRIPE_PRICE_ID,
        stripe_payment_url: DEFAULT_CORE_STRIPE_PAYMENT_URL,
    },
    {
        plan_id: 'custom',
        tier: 'custom',
        name: 'Custom Plan',
        description: 'For multi-location teams or custom finance operations.',
        price_cents: 0,
        interval: 'month',
        features: [
            'Custom workflows: tailored setup for your reporting and approval process.',
            'Advanced support: dedicated help for finance operations and onboarding.',
            'Flexible limits: usage and document capacity matched to your workspace.',
            'Platform rollout: support for teams, locations, and process design.',
        ],
        is_active: true,
        is_recommended: false,
        sort_order: 30,
    },
    {
        plan_id: 'enterprise',
        tier: 'custom',
        name: 'Enterprise Plan',
        description: 'Custom platform rollout for larger teams.',
        price_cents: 0,
        interval: 'month',
        features: [
            'Dedicated finance ops implementation.',
            'Custom reporting and controls.',
            'Expanded workspace limits.',
            'Priority support and rollout planning.',
        ],
        is_active: false,
        is_recommended: false,
        sort_order: 40,
    },
];

export default function AdminBillingPlansModal({ isOpen, workspaceId, businessName, currentPlanId, currentPlanStatus, onClose }: AdminBillingPlansModalProps) {
    const queryClient = useQueryClient();
    const [editingPlan, setEditingPlan] = useState<Partial<BillingPlan>>(emptyPlan);
    const [priceDollars, setPriceDollars] = useState('');
    const [featuresText, setFeaturesText] = useState('');

    const queryKey = useMemo(() => ['adminBillingPlans', workspaceId], [workspaceId]);

    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: () => billingApi.getAdminWorkspacePlans(workspaceId, true),
        enabled: isOpen && Boolean(workspaceId),
    });

    const templatesQuery = useQuery({
        queryKey: ['adminBillingPlanTemplates'],
        queryFn: billingApi.getAdminPlanTemplates,
        enabled: isOpen,
    });

    const resetForm = () => {
        setEditingPlan(emptyPlan);
        setPriceDollars('');
        setFeaturesText('');
    };

    useEffect(() => {
        if (!isOpen) resetForm();
    }, [isOpen]);

    const saveMutation = useMutation({
        mutationFn: (payload: Partial<BillingPlan>) => (
            payload.plan_id
                ? billingApi.updateAdminWorkspacePlan(workspaceId, payload.plan_id, payload)
                : billingApi.createAdminWorkspacePlan(workspaceId, payload)
        ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({ queryKey: ['billingPlans'] });
            resetForm();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (planId: string) => billingApi.deleteAdminWorkspacePlan(workspaceId, planId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({ queryKey: ['billingPlans'] });
        },
    });

    const selectedMutation = useMutation({
        mutationFn: (plan: BillingPlan) => billingApi.updateAdminWorkspacePlan(workspaceId, plan.plan_id, {
            ...plan,
            is_active: !plan.is_active,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({ queryKey: ['billingPlans'] });
            queryClient.invalidateQueries({ queryKey: ['adminBillingOverview'] });
        },
    });

    if (!isOpen) return null;

    const plans = (data?.data || []).map((plan) => hydrateDefaultStripeFields(plan));
    const templates = (templatesQuery.data?.data?.length ? templatesQuery.data.data : PLAN_TEMPLATES)
        .map((template) => hydrateDefaultStripeFields(template));
    const isEditingExisting = Boolean(editingPlan.plan_id);
    const normalizedCurrentPlanStatus = (currentPlanStatus || '').toLowerCase();
    const isCurrentSubscriptionPlan = (plan: BillingPlan) => (
        currentPlanId
            ? plan.plan_id === currentPlanId
            : Boolean(normalizedCurrentPlanStatus && plan.plan_id === normalizedCurrentPlanStatus)
    );

    const handleEdit = (plan: BillingPlan) => {
        const hydratedPlan = hydrateDefaultStripeFields(plan);
        setEditingPlan(hydratedPlan);
        setPriceDollars(dollarsFromCents(hydratedPlan.price_cents));
        setFeaturesText((hydratedPlan.features || []).join('\n'));
    };

    const applyTemplate = (template: Partial<BillingPlan>) => {
        const existing = plans.find((plan) => plan.plan_id === template.plan_id);
        if (existing) {
            handleEdit(hydrateDefaultStripeFields({
                ...existing,
                stripe_price_id: existing.stripe_price_id || template.stripe_price_id,
                stripe_payment_url: existing.stripe_payment_url || template.stripe_payment_url,
            }));
            return;
        }
        const nextPlan = hydrateDefaultStripeFields({
            ...emptyPlan,
            ...template,
            currency: template.currency || 'usd',
            llm_tokens_limit_monthly: template.llm_tokens_limit_monthly || 15_000_000,
            doc_units_limit_monthly: template.doc_units_limit_monthly || 400,
        });
        setEditingPlan(nextPlan);
        setPriceDollars(dollarsFromCents(nextPlan.price_cents));
        setFeaturesText((nextPlan.features || []).join('\n'));
    };

    const handleSave = () => {
        const payload: Partial<BillingPlan> = {
            ...editingPlan,
            price_cents: Math.round(Number(priceDollars || 0) * 100),
            features: featuresText.split('\n').map((item) => item.trim()).filter(Boolean),
            currency: editingPlan.currency || 'usd',
            interval: editingPlan.interval || 'month',
            tier: editingPlan.tier || 'core',
            name: (editingPlan.name || '').trim(),
            description: (editingPlan.description || '').trim(),
            stripe_payment_url: (editingPlan.stripe_payment_url || '').trim(),
            stripe_price_id: (editingPlan.stripe_price_id || '').trim(),
            llm_tokens_limit_monthly: Number(editingPlan.llm_tokens_limit_monthly || 0),
            doc_units_limit_monthly: Number(editingPlan.doc_units_limit_monthly || 0),
            sort_order: Number(editingPlan.sort_order || 100),
        };
        if (!payload.name) {
            alert('Plan name is required.');
            return;
        }
        if (payload.tier !== 'custom' && !payload.stripe_price_id) {
            alert('Stripe price ID is required for webhook mapping.');
            return;
        }
        saveMutation.mutate(payload);
    };

    return (
        <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-slate-900/40 p-4">
            <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Billing Cards</h3>
                        <p className="text-sm text-gray-500">{businessName} · Workspace {workspaceId}</p>
                    </div>
                    <button onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_380px]">
                    <div className="border-b border-gray-200 p-6 lg:border-b-0 lg:border-r">
                        {isLoading ? (
                            <div className="flex h-48 items-center justify-center">
                                <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {plans.map((plan) => (
                                    <div key={plan.plan_id} className={`rounded-xl border p-4 ${plan.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-70'}`}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                                                    {plan.is_recommended && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">Recommended</span>}
                                                    {isCurrentSubscriptionPlan(plan) && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Current plan</span>}
                                                    {!plan.is_active && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">Not visible</span>}
                                                </div>
                                                <p className="mt-1 text-sm text-gray-500">{plan.description}</p>
                                                <p className="mt-2 text-sm font-semibold text-gray-900">${((plan.price_cents || 0) / 100).toLocaleString()} / {plan.interval}</p>
                                                <p className="mt-1 text-xs text-gray-500">Stripe price: {plan.stripe_price_id || 'missing'}</p>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-2">
                                                <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={plan.is_active !== false}
                                                        disabled={selectedMutation.isPending || (isCurrentSubscriptionPlan(plan) && plan.is_active !== false)}
                                                        onChange={() => selectedMutation.mutate(plan)}
                                                    />
                                                    Visible
                                                </label>
                                                <button onClick={() => handleEdit(plan)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm(`Hide ${plan.name} from billing page?`)) deleteMutation.mutate(plan.plan_id);
                                                    }}
                                                    disabled={isCurrentSubscriptionPlan(plan) && plan.is_active !== false}
                                                    className="rounded-lg border border-red-200 px-2 py-1.5 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {plans.length === 0 && <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">No billing cards yet.</div>}
                            </div>
                        )}
                    </div>

                    <div className="p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h4 className="font-semibold text-gray-900">{isEditingExisting ? 'Edit card' : 'Add card'}</h4>
                            {isEditingExisting && (
                                <button onClick={resetForm} className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600">
                                    <Plus className="h-4 w-4" />
                                    New
                                </button>
                            )}
                        </div>
                        <div className="mb-4">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Templates</p>
                            <div className="grid grid-cols-2 gap-2">
                                {templates.map((template) => (
                                    <button
                                        key={template.plan_id}
                                        type="button"
                                        onClick={() => applyTemplate(template)}
                                        className="rounded-lg border border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700 hover:border-indigo-300 hover:bg-indigo-50"
                                    >
                                        {template.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-3">
                            <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Plan name" value={editingPlan.name || ''} onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })} />
                            <textarea className="min-h-[70px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Description" value={editingPlan.description || ''} onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })} />
                            <div className="grid grid-cols-2 gap-3">
                                <input className="rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Price, e.g. 499" value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)} />
                                <select className="rounded-lg border border-gray-300 px-3 py-2 text-sm" value={editingPlan.tier || 'core'} onChange={(e) => setEditingPlan({ ...editingPlan, tier: e.target.value })}>
                                    <option value="standard">Standard tier</option>
                                    <option value="core">Core tier</option>
                                    <option value="custom">Custom / Talk to Finz</option>
                                </select>
                            </div>
                            <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Stripe payment URL" value={editingPlan.stripe_payment_url || ''} onChange={(e) => setEditingPlan({ ...editingPlan, stripe_payment_url: e.target.value })} />
                            <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Stripe price ID, e.g. price_..." value={editingPlan.stripe_price_id || ''} onChange={(e) => setEditingPlan({ ...editingPlan, stripe_price_id: e.target.value })} />
                            <textarea className="min-h-[120px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Features, one per line" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} />
                            <div className="grid grid-cols-2 gap-3">
                                <input className="rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="LLM token limit" type="number" value={editingPlan.llm_tokens_limit_monthly || 0} onChange={(e) => setEditingPlan({ ...editingPlan, llm_tokens_limit_monthly: Number(e.target.value) })} />
                                <input className="rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Doc unit limit" type="number" value={editingPlan.doc_units_limit_monthly || 0} onChange={(e) => setEditingPlan({ ...editingPlan, doc_units_limit_monthly: Number(e.target.value) })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input className="rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Sort order" type="number" value={editingPlan.sort_order || 100} onChange={(e) => setEditingPlan({ ...editingPlan, sort_order: Number(e.target.value) })} />
                                <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm">
                                    <input type="checkbox" checked={Boolean(editingPlan.is_recommended)} onChange={(e) => setEditingPlan({ ...editingPlan, is_recommended: e.target.checked })} />
                                    Recommended card
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">Only one plan can be recommended per workspace. Saving this card as recommended will clear the badge from the others.</p>
                            <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm">
                                <input type="checkbox" checked={editingPlan.is_active !== false} onChange={(e) => setEditingPlan({ ...editingPlan, is_active: e.target.checked })} />
                                Selected: show this card on billing page
                            </label>
                            <button
                                onClick={handleSave}
                                disabled={saveMutation.isPending}
                                className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                                {isEditingExisting ? 'Save card' : 'Add card'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
